/*─────────────────────────────────────────────────────────────────────────────┐
│  src/pages/WebsiteGeneratorPage.jsx – Bootstrap edition                      │
│                                                                              │
│  ★  Drag-and-drop landing-page builder with live preview & ZIP export        │
│  ★  Generates pure Bootstrap-5 markup                                        │
│  ★  Sidebar ▸ Builder ▸ Live Preview, undo/redo, duplicate, hide/show        │
└─────────────────────────────────────────────────────────────────────────────*/

/* eslint-disable react/jsx-no-bind */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Input,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  DragIndicator as GripIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { motion } from 'framer-motion';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
// import Navbar from '../components/Navbar';
// import Footer from '../components/Footer';

/*──────────────────────── 1. SECTION TEMPLATES ─────────────────────────────*/
const TEMPLATES = [
  /* ––– NAVBAR ––– */
  {
    type: 'Navbar',
    title: 'Navigation Bar',
    default: {
      logo: null,
      links: ['Home', 'About', 'Services', 'Projects', 'Contact'],
      bgColor: '#ffffff',
      textColor: '#333333',
      sticky: true,
      visible: true
    }
  },
  /* ––– HERO ––– */
  {
    type: 'Hero',
    title: 'Hero Header',
    default: {
      headline: 'Welcome to Lemda',
      sub: 'Building next-gen web & mobile experiences',
      ctaText: 'Get Started',
      ctaUrl: '#contact',
      bgColor: '#0d6efd',
      textColor: '#ffffff',
      bgImage: null,
      textAlign: 'center',
      height: '80vh',
      visible: true
    }
  },
  /* ––– SERVICES ––– */
  {
    type: 'Services',
    title: 'Services Grid',
    default: {
      sectionTitle: 'Our Expertise',
      items: [
        { title: 'Web&nbsp;Development', desc: 'React, Next.js', bgColor: '#e9f5ff' },
        { title: 'Mobile&nbsp;Apps', desc: 'React&nbsp;Native, Expo', bgColor: '#fff0f6' },
        { title: 'AI&nbsp;& GPTs', desc: 'Custom chatbots', bgColor: '#f3f0ff' }
      ],
      bgColor: '#fafafa',
      textColor: '#333',
      cols: 3,
      visible: true
    }
  },
  /* ––– FEATURES ––– */
  {
    type: 'Features',
    title: 'Features 2-Col',
    default: {
      sectionTitle: 'Why Choose Us',
      left: 'Scalable & maintainable code',
      right: '24/7 monitoring & support',
      bgColor: '#ffffff',
      textColor: '#333333',
      visible: true
    }
  },
  /* ––– PRICING ––– */
  {
    type: 'Pricing',
    title: 'Pricing Plans',
    default: {
      sectionTitle: 'Our Packages',
      plans: [
        { name: 'Starter', price: '$99', features: ['5 pages', 'Email support'], recommended: false },
        { name: 'Growth', price: '$299', features: ['15 pages', 'Chat support'], recommended: true },
        { name: 'Pro', price: '$799', features: ['Unlimited', 'Dedicated manager'], recommended: false }
      ],
      bgColor: '#f7f7f7',
      textColor: '#333',
      visible: true
    }
  },
  /* ––– FAQ ––– */
  {
    type: 'FAQ',
    title: 'FAQ Accordion',
    default: {
      sectionTitle: 'Frequently Asked Questions',
      items: [
        { q: 'How long to build a site?', a: 'Typically 2–4 weeks.' },
        { q: 'Do you offer support?', a: 'Yes, 6 months free support.' }
      ],
      bgColor: '#ffffff',
      textColor: '#333',
      visible: true
    }
  },
  /* ––– STATS ––– */
  {
    type: 'Stats',
    title: 'Stats Counters',
    default: {
      sectionTitle: 'Our Achievements',
      stats: [
        { label: 'Projects', value: '120+' },
        { label: 'Clients', value: '75+' },
        { label: 'Coffee', value: '999+' }
      ],
      bgColor: '#e8f5e9',
      textColor: '#2e7d32',
      visible: true
    }
  },
  /* ––– TEAM ––– */
  {
    type: 'Team',
    title: 'Team Profiles',
    default: {
      sectionTitle: 'Meet the Team',
      members: [
        { name: 'Alice', role: 'CEO', photo: null },
        { name: 'Bob', role: 'CTO', photo: null }
      ],
      bgColor: '#ffffff',
      textColor: '#333333',
      visible: true
    }
  },
  /* ––– TESTIMONIALS ––– */
  {
    type: 'Testimonials',
    title: 'Testimonials',
    default: {
      sectionTitle: 'What Clients Say',
      quotes: [
        { text: 'Excellent work!', author: 'Client A' },
        { text: 'Superb support.', author: 'Client B' }
      ],
      bgColor: '#fafafa',
      textColor: '#333',
      visible: true
    }
  },
  /* ––– PARTNERS ––– */
  {
    type: 'Partners',
    title: 'Partners Logos',
    default: {
      sectionTitle: 'Trusted By',
      logos: [null, null, null, null],
      bgColor: '#ffffff',
      textColor: '#333',
      visible: true
    }
  },
  /* ––– CTA ––– */
  {
    type: 'CTA',
    title: 'Call-To-Action',
    default: {
      text: 'Ready to kick-off your project?',
      buttonText: 'Contact Us',
      buttonUrl: '#contact',
      bgColor: '#ff5722',
      textColor: '#ffffff',
      visible: true
    }
  },
  /* ––– CONTACT ––– */
  {
    type: 'Contact',
    title: 'Contact Form',
    default: {
      sectionTitle: 'Get in Touch',
      bgColor: '#ffffff',
      textColor: '#333333',
      visible: true
    }
  },
  /* ––– FOOTER ––– */
  {
    type: 'Footer',
    title: 'Footer',
    default: {
      text: '© 2025 Lemda Pvt Ltd. All rights reserved.',
      links: ['Privacy', 'Terms', 'Sitemap'],
      bgColor: '#333333',
      textColor: '#ffffff',
      visible: true
    }
  }
];

/*──────────────────────── 2. HELPERS ───────────────────────────────────────*/
const toBase64 = f =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });
const MotionPaper = motion(Paper);

/*──────────────────────── 3. MAIN COMPONENT ───────────────────────────────*/
export default function WebsiteGeneratorPage() {
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));

  /* state */
  const [ws, setWs] = useState([]);
  const [id, setId] = useState(1);
  const [toast, setToast] = useState('');
  const [hist, setHist] = useState({ past: [], future: [] });
  const isMobile = useMediaQuery('(max-width:960px)');


  /* undo / redo */
  const commit = next => {
    setHist(({ past }) => ({ past: [...past, ws], future: [] }));
    setWs(next);
  };
  const undo = () => hist.past.length && (setWs(hist.past.at(-1)), setHist(({ past, future }) => ({ past: past.slice(0, -1), future: [ws, ...future] })));
  const redo = () => hist.future.length && (setWs(hist.future[0]), setHist(({ past, future }) => ({ past: [...past, ws], future: future.slice(1) })));

  /* drag-and-drop */
  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    if (source.droppableId === 'SIDEBAR' && destination.droppableId === 'CANVAS') {
      const tpl = TEMPLATES.find(t => t.type === draggableId);
      const inst = { id: `${draggableId}-${id}`, type: tpl.type, content: structuredClone(tpl.default) };
      commit([...ws.slice(0, destination.index), inst, ...ws.slice(destination.index)]);
      setId(n => n + 1);
      return;
    }
    if (source.droppableId === 'CANVAS' && destination.droppableId === 'CANVAS') {
      const arr = [...ws];
      const [mov] = arr.splice(source.index, 1);
      arr.splice(destination.index, 0, mov);
      commit(arr);
    }
  };

  /* update nested field */
  const setField = (sid, key, val) =>
    commit(
      ws.map(sec =>
        sec.id === sid ? { ...sec, content: { ...sec.content, [key]: val } } : sec
      )
    );

  /*──────────────────  BUILD BOOTSTRAP HTML  ──────────────────*/
  const html = useMemo(() => {
    const head = `
<!doctype html><html lang="en"><head>
<meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Lemda Landing</title>
<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet"/>

<style>html,body{scroll-behavior:smooth}.hero-full{min-height:80vh}</style>
</head><body>`;
    const tail = `<script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js"></script></body></html>`;

    let uid = 0;
    const uidFn = () => `uid${uid++}`;

    const body = ws
      .filter(s => s.content.visible)
      .map(sec => {
        const c = sec.content;
        switch (sec.type) {
          /* NAVBAR */
          case 'Navbar':
            return `
<nav class="navbar navbar-expand-lg py-3" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <a class="navbar-brand fw-bold" href="#">${c.logo ? `<img src="${c.logo}" height="40"/>` : 'Lemda'}</a>
    <button class="navbar-toggler" data-bs-toggle="collapse" data-bs-target="#${uidFn()}"><span class="navbar-toggler-icon"></span></button>
    <div class="collapse navbar-collapse" id="${uidFn()}">
      <ul class="navbar-nav ms-auto mb-2 mb-lg-0">
        ${c.links.map(l => `<li class="nav-item"><a class="nav-link" href="#${l.toLowerCase()}">${l}</a></li>`).join('')}
      </ul>
    </div>
  </div>
</nav>`;
          /* HERO */
          case 'Hero':
            return `
<section id="hero" class="hero-full d-flex align-items-center text-${c.textAlign} ${c.bgImage ? '' : 'bg-primary'}" style="${c.bgImage ? `background:url('${c.bgImage}') center/cover` : `background:${c.bgColor}`};color:${c.textColor}">
  <div class="container">
    <h1 class="display-4 fw-bold">${c.headline}</h1>
    <p class="lead mb-4">${c.sub}</p>
    <a href="${c.ctaUrl}" class="btn btn-light btn-lg">${c.ctaText}</a>
  </div>
</section>`;
          /* SERVICES */
          case 'Services':
            return `
<section id="services" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container text-center">
    <h2 class="fw-bold mb-5">${c.sectionTitle}</h2>
    <div class="row g-4">
      ${c.items
        .map(
          it => `<div class="col-md-${12 / c.cols}">
        <div class="card h-100 border-0 shadow-sm" style="background:${it.bgColor}">
          <div class="card-body">
            <h5 class="card-title">${it.title}</h5>
            <p class="card-text">${it.desc}</p>
          </div>
        </div>
      </div>`
        )
        .join('')}
    </div>
  </div>
</section>`;
          /* FEATURES */
          case 'Features':
            return `
<section id="features" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <div class="row align-items-center g-4">
      <div class="col-md-6">
        <h2 class="fw-bold mb-3">${c.sectionTitle}</h2>
        <p>${c.left}</p>
      </div>
      <div class="col-md-6">
        <p>${c.right}</p>
      </div>
    </div>
  </div>
</section>`;
          /* PRICING */
          case 'Pricing':
            return `
<section id="pricing" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container text-center">
    <h2 class="fw-bold mb-5">${c.sectionTitle}</h2>
    <div class="row row-cols-1 row-cols-md-3 g-4">
      ${c.plans
        .map(
          p => `<div class="col">
        <div class="card h-100 ${p.recommended ? 'border-primary border-3' : 'border-0'} shadow-sm">
          <div class="card-body d-flex flex-column">
            <h5 class="card-title">${p.name}</h5>
            <h3 class="my-3">${p.price}</h3>
            <ul class="list-unstyled flex-grow-1">${p.features.map(f => `<li class="mb-2">${f}</li>`).join('')}</ul>
            <a href="#contact" class="btn btn-${p.recommended ? 'primary' : 'outline-primary'} mt-auto">Choose</a>
          </div>
        </div>
      </div>`
        )
        .join('')}
    </div>
  </div>
</section>`;
          /* FAQ */
          case 'FAQ': {
            const acc = uidFn();
            return `
<section id="faq" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <h2 class="fw-bold text-center mb-4">${c.sectionTitle}</h2>
    <div class="accordion" id="${acc}">
      ${c.items
        .map(
          (it, i) => `<div class="accordion-item">
        <h2 class="accordion-header" id="${acc}h${i}">
          <button class="accordion-button collapsed" data-bs-toggle="collapse" data-bs-target="#${acc}c${i}">${it.q}</button>
        </h2>
        <div id="${acc}c${i}" class="accordion-collapse collapse" data-bs-parent="#${acc}">
          <div class="accordion-body">${it.a}</div>
        </div>
      </div>`
        )
        .join('')}
    </div>
  </div>
</section>`;
          }
          /* STATS */
          case 'Stats':
            return `
<section id="stats" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container text-center">
    <h2 class="fw-bold mb-4">${c.sectionTitle}</h2>
    <div class="row g-4">
      ${c.stats
        .map(
          s => `<div class="col-md-4">
        <h3 class="display-5 fw-bold">${s.value}</h3>
        <p class="mb-0">${s.label}</p>
      </div>`
        )
        .join('')}
    </div>
  </div>
</section>`;
          /* TEAM */
          case 'Team':
            return `
<section id="team" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container text-center">
    <h2 class="fw-bold mb-5">${c.sectionTitle}</h2>
    <div class="row g-4">
      ${c.members
        .map(
          m => `<div class="col-md-3">
        <div class="card h-100 border-0 shadow-sm">
          <img src="${m.photo || `https://i.pravatar.cc/150?u=${m.name}`}" class="card-img-top" alt="${m.name}">
          <div class="card-body">
            <h5 class="card-title">${m.name}</h5>
            <p class="card-text">${m.role}</p>
          </div>
        </div>
      </div>`
        )
        .join('')}
    </div>
  </div>
</section>`;
          /* TESTIMONIALS */
          case 'Testimonials': {
            const car = uidFn();
            return `
<section id="testimonials" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <h2 class="fw-bold text-center mb-4">${c.sectionTitle}</h2>
    <div id="${car}" class="carousel slide" data-bs-ride="carousel">
      <div class="carousel-inner">
        ${c.quotes
          .map(
            (q, i) => `<div class="carousel-item ${i === 0 ? 'active' : ''}">
          <blockquote class="blockquote text-center">
            <p class="fs-4 mb-4">“${q.text}”</p>
            <footer class="blockquote-footer">${q.author}</footer>
          </blockquote>
        </div>`
          )
          .join('')}
      </div>
      <button class="carousel-control-prev" type="button" data-bs-target="#${car}" data-bs-slide="prev"><span class="carousel-control-prev-icon"></span></button>
      <button class="carousel-control-next" type="button" data-bs-target="#${car}" data-bs-slide="next"><span class="carousel-control-next-icon"></span></button>
    </div>
  </div>
</section>`;
          }
          /* PARTNERS */
          case 'Partners':
            return `
<section id="partners" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container text-center">
    <h2 class="fw-bold mb-4">${c.sectionTitle}</h2>
    <div class="d-flex flex-wrap justify-content-center gap-4">
      ${c.logos
        .map(
          l =>
            `<img src="${l || 'https://via.placeholder.com/120x60?text=Logo'}" class="img-fluid opacity-75" style="max-height:60px" alt="logo">`
        )
        .join('')}
    </div>
  </div>
</section>`;
          /* CTA */
          case 'CTA':
            return `
<section id="cta" class="py-5 text-center" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <h2 class="fw-bold mb-4">${c.text}</h2>
    <a href="${c.buttonUrl}" class="btn btn-light btn-lg">${c.buttonText}</a>
  </div>
</section>`;
          /* CONTACT */
          case 'Contact':
            return `
<section id="contact" class="py-5" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <h2 class="fw-bold text-center mb-4">${c.sectionTitle}</h2>
    <form class="mx-auto" style="max-width:500px">
      <div class="mb-3"><input class="form-control" placeholder="Name"/></div>
      <div class="mb-3"><input type="email" class="form-control" placeholder="Email"/></div>
      <div class="mb-3"><textarea rows="4" class="form-control" placeholder="Message"></textarea></div>
      <button class="btn btn-primary w-100" type="submit">Send</button>
    </form>
  </div>
</section>`;
          /* FOOTER */
          case 'Footer':
            return `
<footer class="py-4 text-center" style="background:${c.bgColor};color:${c.textColor}">
  <div class="container">
    <p class="mb-2">${c.text}</p>
    <nav>${c.links.map(l => `<a class="link-light mx-2" href="#">${l}</a>`).join('')}</nav>
  </div>
</footer>`;
          default:
            return '';
        }
      })
      .join('\n');

    return head + body + tail;
  }, [ws]);

  /* ZIP */
  const downloadZip = async () => {
    setToast('Building ZIP…');
    const z = new JSZip();
    z.file('index.html', html);
    saveAs(await z.generateAsync({ type: 'blob' }), 'landing-page.zip');
    setToast('ZIP ready!');
  };

  /* generic field */
  const field = (sid, k, v) => {
    if (k === 'visible') return null;
    if (typeof v === 'string' && /(img|image|logo|photo|bgImage)$/i.test(k))
      return (
        <Box key={k} mb={1}>
          <Typography variant="body2">{k}</Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Input
              type="file"
              onChange={async e => {
                const f = e.target.files[0];
                if (f) setField(sid, k, await toBase64(f));
              }}
            />
            {v && <Box component="img" src={v} sx={{ width: 48, height: 48 }} />}
          </Box>
        </Box>
      );
    if (/color$/i.test(k))
      return (
        <Box key={k} mb={1}>
          <Typography variant="body2">{k}</Typography>
          <Input type="color" value={v} onChange={e => setField(sid, k, e.target.value)} />
        </Box>
      );
    if (Array.isArray(v))
      return (
        <Box key={k} mb={1}>
          <Typography variant="body2">{k}</Typography>
          {v.map((val, i) => (
            <Box key={i} display="flex" gap={1} mb={0.5}>
              <TextField
                size="small"
                fullWidth
                value={typeof val === 'object' ? JSON.stringify(val) : val}
                onChange={e => {
                  const arr = [...v];
                  arr[i] = e.target.value;
                  setField(sid, k, arr);
                }}
              />
              <IconButton size="small" onClick={() => setField(sid, k, v.filter((_, j) => j !== i))}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => setField(sid, k, [...v, 'New item'])}>
            Add
          </Button>
        </Box>
      );
    if (k === 'textAlign')
      return (
        <FormControl key={k} fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>Align</InputLabel>
          <Select value={v} label="Align" onChange={e => setField(sid, k, e.target.value)}>
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </FormControl>
      );
    return (
      <Box key={k} mb={1}>
        <Typography variant="body2">{k}</Typography>
        <TextField size="small" fullWidth value={v} onChange={e => setField(sid, k, e.target.value)} />
      </Box>
    );
  };

  /*──────────────────────── RENDER UI ─────────────────────────*/
return (
  <>
    {/* <Navbar /> */}
    {isMobile && (
  <Box
    sx={{
      bgcolor: '#fff3cd',
      color: '#856404',
      p: 2,
      mb: 2,
      border: '1px solid #ffeeba',
      borderRadius: 2,
      textAlign: 'center',
    }}
  >
    <Typography variant="body1" fontWeight="bold">
      ⚠️ Please use a PC or tablet for the best website building experience.
    </Typography>
    <Typography variant="body2">
      The drag-and-drop interface is optimized for larger screens.
    </Typography>
  </Box>
)}


    <DragDropContext onDragEnd={onDragEnd}>
      <Box display="flex" gap={2} p={5} sx={{ height: 'calc(100vh - 160px)' }}>
        {/* SIDEBAR – 10% */}
        <Box
          sx={{
            width: '10%',
            minWidth: 150,
            position: 'sticky',
            top: 80,
            overflowY: 'auto',
            borderRight: `1px solid ${theme.palette.divider}`,
            pr: 1,
          }}
        >
          <Droppable droppableId="SIDEBAR">
            {p => (
              <Box ref={p.innerRef} {...p.droppableProps}>
                <Typography variant="h6" gutterBottom>Sections</Typography>
                <Divider sx={{ mb: 1 }} />
                {TEMPLATES.map((tpl, i) => (
                  <Draggable key={tpl.type} draggableId={tpl.type} index={i}>
                    {d => (
                      <MotionPaper
                        ref={d.innerRef}
                        {...d.draggableProps}
                        {...d.dragHandleProps}
                        whileHover={{ scale: 1.02 }}
                        sx={{ p: 1, mb: 1, cursor: 'grab', bgcolor: theme.palette.action.hover }}
                      >
                        {tpl.title}
                      </MotionPaper>
                    )}
                  </Draggable>
                ))}
                {p.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>

        {/* CANVAS – 40% */}
        <Box
          sx={{
            width: '40%',
            overflowY: 'auto',
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
          }}
        >
          <Droppable droppableId="CANVAS">
            {p => (
              <Box ref={p.innerRef} {...p.droppableProps}>
                {!ws.length && (
                  <Typography align="center" color="text.secondary">
                    Drag sections here…
                  </Typography>
                )}
                {ws.map((sec, i) => (
                  <Draggable key={sec.id} draggableId={sec.id} index={i}>
                    {d => (
                      <MotionPaper
                        ref={d.innerRef}
                        {...d.draggableProps}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.25 }}
                        sx={{ mb: 2 }}
                      >
                        <Box display="flex" alignItems="center" bgcolor={theme.palette.action.hover} px={1}>
                          <Box {...d.dragHandleProps} sx={{ cursor: 'grab', mr: 1 }}>
                            <GripIcon fontSize="small" />
                          </Box>
                          <Typography sx={{ flexGrow: 1 }}>{sec.type}</Typography>
                          <Tooltip title={sec.content.visible ? 'Hide' : 'Show'}>
                            <IconButton size="small" onClick={() => setField(sec.id, 'visible', !sec.content.visible)}>
                              {sec.content.visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton size="small" onClick={() => {
                              commit([...ws.slice(0, i + 1), { ...sec, id: `${sec.type}-${id}` }, ...ws.slice(i + 1)]);
                              setId(n => n + 1);
                            }}>
                              <DuplicateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => commit(ws.filter(s => s.id !== sec.id))}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box p={2}>
                          {Object.entries(sec.content).map(([k, v]) => field(sec.id, k, v))}
                        </Box>
                      </MotionPaper>
                    )}
                  </Draggable>
                ))}
                {p.placeholder}
              </Box>
            )}
          </Droppable>

          {/* Buttons */}
          <Box mt={2} display="flex" gap={1}>
            <Button variant="outlined" startIcon={<UndoIcon />} onClick={undo} disabled={!hist.past.length}>Undo</Button>
            <Button variant="outlined" startIcon={<RedoIcon />} onClick={redo} disabled={!hist.future.length}>Redo</Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadZip} disabled={!ws.length}>Export ZIP</Button>
          </Box>
        </Box>

        {/* PREVIEW – 40% */}
        <Box
          sx={{
            width: '40%',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'sticky',
            top: 80,
            height: 'calc(100vh - 160px)',
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box bgcolor={theme.palette.action.hover} p={1}>
            <Typography variant="subtitle2">Live Preview</Typography>
          </Box>
          <Box
            component="iframe"
            srcDoc={html}
            title="preview"
            style={{ flexGrow: 1, border: 'none', width: '100%' }}
          />
        </Box>
      </Box>
    </DragDropContext>

    {/* Auto-save badge */}
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        bgcolor: theme.palette.success.main,
        color: '#fff',
        px: 2,
        py: 0.5,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <SaveIcon fontSize="small" />
      <Typography variant="caption">Changes auto-saved</Typography>
    </Box>

    {/* Toast */}
    <Snackbar
      open={!!toast}
      autoHideDuration={3000}
      onClose={() => setToast('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity="success" variant="filled">{toast}</Alert>
    </Snackbar>

    {/* <Footer /> */}
  </>
);



}
